import type { Announcement } from '../api/types';
import { ANNOUNCEMENT_PRODUCT_NAME } from './productName';

/**
 * Pure rules for the Announcements badge and page (docs/42 §4, §5). Data in, data out, so every
 * edge is testable without a query client or a DOM.
 *
 * The seen mark is `announcementsSeenAt`, an ISO instant. Unread means a VISIBLE item whose
 * `publishedAt` is strictly after it. Strictly, because the mark is written as the exact
 * `publishedAt` of the newest item rendered, so `>=` would leave that item unread forever.
 */

/** No stored mark counts as this long ago, so a user at launch sees the October backfill badged
 *  (docs/42 §5). The nav item has to reach prod within this window of the backfill. */
export const DEFAULT_SEEN_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/** Epoch ms of an ISO instant, or null when absent or unparseable. */
function ts(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/**
 * The instant to compare against. A missing mark is `now - 30 days`. An UNPARSEABLE stored mark
 * is treated the same as a missing one: a garbled value should make items look unread (harmless,
 * and the next visit rewrites it) rather than read forever.
 */
export function effectiveSeenAt(seenAt: string | null | undefined, now: number): number {
  return ts(seenAt) ?? now - DEFAULT_SEEN_WINDOW_MS;
}

/**
 * The items this host may show. `hideOnBrandedHosts` is a presentation choice, not access
 * control (docs/42 §4), so it is applied here in the client. The count and the page both go
 * through this, so they cannot disagree.
 */
export function visibleAnnouncements(items: readonly Announcement[], isWhiteLabel: boolean): Announcement[] {
  return items.filter(a => !(isWhiteLabel && a.hideOnBrandedHosts));
}

/** Newest first: `displayDate`, then `publishedAt` (the order appcore documents). */
export function sortAnnouncements(items: readonly Announcement[]): Announcement[] {
  return [...items].sort((a, b) =>
    (b.displayDate < a.displayDate ? -1 : b.displayDate > a.displayDate ? 1 : 0)
    || ((ts(b.publishedAt) ?? 0) - (ts(a.publishedAt) ?? 0)));
}

/** Is `item` unread against a threshold from {@link effectiveSeenAt}? Unparseable `publishedAt`
 *  is "no information" and reads as read, so a malformed row can never pin the badge on. */
export function isAnnouncementUnread(item: Announcement, seenAtMs: number): boolean {
  const published = ts(item.publishedAt);
  return published !== null && published > seenAtMs;
}

/** The nav badge count. Family-only items count in a firm workspace on purpose (docs/42 §5). */
export function countUnreadAnnouncements(
  items: readonly Announcement[],
  seenAt: string | null | undefined,
  now: number,
): number {
  const threshold = effectiveSeenAt(seenAt, now);
  let n = 0;
  for (const item of items) if (isAnnouncementUnread(item, threshold)) n++;
  return n;
}

/** The `publishedAt` to store: the newest one among `rendered`, as the original string. Null when
 *  nothing is rendered, or when nothing is newer than the mark already stored (no write). */
export function nextSeenAt(
  rendered: readonly Announcement[],
  seenAt: string | null | undefined,
): string | null {
  let newest: string | null = null;
  let newestTs = -Infinity;
  for (const a of rendered) {
    const t = ts(a.publishedAt);
    if (t === null || t <= newestTs) continue;
    newestTs = t;
    newest = a.publishedAt;
  }
  if (newest === null) return null;
  const stored = ts(seenAt);
  if (stored !== null && newestTs <= stored) return null;
  return newest;
}

/** A family-only item shown to a firm. `viewerAudience` is appcore's answer, never a guess. */
export function isForYourClients(item: Announcement, viewerAudience: 'families' | 'firms'): boolean {
  return viewerAudience === 'firms' && !item.audience.includes('firms');
}

/** The nav item's accessible name. The badge glyph is `aria-hidden`, so the count has to live
 *  here or the feature is invisible to a screen reader (the `feedbackNavLabel` reasoning). */
export function announcementsNavLabel(count: number): string {
  return count <= 0 ? 'Announcements' : `Announcements, ${count} new`;
}

/**
 * `{product}` into plain text, always "Steward" (`ANNOUNCEMENT_PRODUCT_NAME`). Split and join
 * rather than `replace`, so the replacement is never read as a `$` pattern.
 */
export function substituteProduct(text: string): string {
  return text.split('{product}').join(ANNOUNCEMENT_PRODUCT_NAME);
}

/** `YYYY-MM-DD` (or an ISO instant starting with one) as "October 27, 2026". Read as a calendar
 *  date in UTC, so no timezone can move it a day. Anything else is returned as given. */
export function formatDisplayDate(displayDate: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(displayDate);
  if (!m) return displayDate;
  const d = new Date(`${m[1]}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return displayDate;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}
